import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useToast } from '@/shared/hooks/useToast';
import { useTenantWorkSchedule } from '../hooks/useTenantWorkSchedule';
import { useMemberWorkSchedule } from '../hooks/useMemberWorkSchedule';
import { useUpsertMemberWorkSchedule } from '../hooks/useUpsertMemberWorkSchedule';
import { useDeleteMemberWorkSchedule } from '../hooks/useDeleteMemberWorkSchedule';
import {
  createMemberWorkScheduleSchema,
  type MemberWorkScheduleFormInput,
  type MemberWorkScheduleFormValues,
} from '../schemas';
import { MemberWorkScheduleDialogBody } from './MemberWorkScheduleDialogBody';
import type { WorkSegmentDto } from '../types';

const EMPTY_SEGMENTS = [{ startTime: '', endTime: '' }];

/** Sorts by `order` and drops it — the form never edits `order` directly. */
function toFormSegments(segments: WorkSegmentDto[] | null | undefined) {
  if (!segments || segments.length === 0) return EMPTY_SEGMENTS;
  return [...segments]
    .sort((a, b) => a.order - b.order)
    .map((s) => ({ startTime: s.startTime ?? '', endTime: s.endTime ?? '' }));
}

interface MemberWorkScheduleDialogProps {
  member: { userId: string; displayName?: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MemberWorkScheduleDialog({
  member,
  open,
  onOpenChange,
}: MemberWorkScheduleDialogProps) {
  const { t } = useTranslation('workSchedules');
  const { toast } = useToast();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const name = member.displayName || t('overrides.memberLabel', { userId: member.userId });

  const tenantQuery = useTenantWorkSchedule();
  const memberQuery = useMemberWorkSchedule(member.userId, open);
  const upsert = useUpsertMemberWorkSchedule();
  const remove = useDeleteMemberWorkSchedule();

  const hasOverride = !!memberQuery.data;
  const defaultHours = tenantQuery.data?.hoursPerDay;

  const { control, handleSubmit, reset } = useForm<
    MemberWorkScheduleFormInput,
    unknown,
    MemberWorkScheduleFormValues
  >({
    resolver: zodResolver(createMemberWorkScheduleSchema(t)),
    defaultValues: { segments: EMPTY_SEGMENTS },
  });

  // Prefill the form once data settles: the member's own override if it exists,
  // otherwise the tenant default so editing starts from the inherited value
  // instead of an empty field. Runs only while the dialog is open.
  useEffect(() => {
    if (!open) return;
    const segments = memberQuery.data?.segments ?? tenantQuery.data?.segments;
    reset({ segments: toFormSegments(segments) });
  }, [open, memberQuery.data, tenantQuery.data, reset]);

  const isFetching = memberQuery.isLoading || tenantQuery.isLoading;

  const onSubmit = async (data: MemberWorkScheduleFormValues) => {
    try {
      await upsert.mutateAsync({
        userId: member.userId,
        payload: { segments: data.segments.map((s, order) => ({ order, ...s })) },
      });
      toast.success({
        title: t('member.savedToast'),
        description: t('member.savedToastDesc', { name }),
      });
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  const handleDelete = async () => {
    const result = await confirm({
      title: t('member.deleteConfirm'),
      description: t('member.deleteConfirmDesc'),
      confirmLabel: t('member.delete'),
      cancelLabel: t('member.cancel'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;

    try {
      await remove.mutateAsync(member.userId);
      toast.success({
        title: t('member.deletedToast'),
        description: t('member.deletedToastDesc', { name }),
      });
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-full max-w-none rounded-none md:max-w-105 md:rounded-lg"
          data-testid="work-schedules-member-dialog"
        >
          <DialogHeader>
            <DialogTitle>{t('member.title')}</DialogTitle>
            <DialogDescription>{t('member.description', { name })}</DialogDescription>
          </DialogHeader>

          <MemberWorkScheduleDialogBody
            isFetching={isFetching}
            isError={memberQuery.isError}
            onRetry={() => memberQuery.refetch()}
            hasOverride={hasOverride}
            defaultHours={defaultHours}
            control={control}
            onSubmit={handleSubmit(onSubmit)}
          />

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {hasOverride && !isFetching && (
              <Button
                type="button"
                variant="outline"
                className="text-destructive hover:text-destructive"
                disabled={remove.isPending}
                onClick={handleDelete}
                data-testid="work-schedules-member-delete-button"
              >
                {t('member.delete')}
              </Button>
            )}
            <Button
              type="submit"
              form="member-work-schedule-form"
              disabled={isFetching || upsert.isPending}
              className="sm:ml-auto"
              data-testid="work-schedules-member-save-button"
            >
              {upsert.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {upsert.isPending ? t('member.saving') : t('member.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(o) => !o && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
